import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction, SortDirection } from "@outpave/ds";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import { InvoiceTableQueryProps } from "../../../pages/admin/InvoiceHistory";
import { GET_INVOICES } from "../../../graphql/invoice";
import RenderInvoiceStatus from "../../common/RenderInvoiceStatus";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import RenderDateWithPopup from "../../common/RenderDateWithPopup";
import InvoicePreviewContainer from "./InvoicePreviewContainer";
import { InvoiceStatus } from "../../../enums/InvoiceStatus";
import InvoiceDetailsDrawer from "./InvoiceDetailsDrawer";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useInvoiceData } from "hooks/useInvoiceData";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { getInvoiceStatusDetails } from "hooks/useGetInvoiceStatusDetails";
import DisputeStatus from "enums/DisputeStatus";
import {
  EditInvoiceConfirmationModal,
  RefundInvoiceConfirmationModal,
  ResendFailedPaymentNoticeModal,
  SendInvoiceAsEstimateConfirmationModal,
  SendInvoiceConfirmationModal,
} from "./InvoiceModules";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowTurnBackwardIcon,
  CancelCircleIcon,
  DeletePutBackIcon,
  Estimate01Icon,
  Invoice03Icon,
  Mail01Icon,
  PencilEdit02Icon,
  RecoveryMailIcon,
  ViewIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { mapInvoiceNodeToTableRow } from "utils/invoices/invoiceFormat";
import TransactionStatus from "enums/TransactionStatus";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import useCanTakePayments from "hooks/useCanTakePayments";

type Data = {
  id: string;
  invoiceDate: string;
  sentDate: string;
  paidDate: string;
  recipient: string;
  amount: string;
  invoice: string;
  sentBy: string;
  dueDate: string;
  status: InvoiceStatus;
  paymentTerms?: string;
  microdepositPending?: boolean;
  recipientEmail?: string;
  primaryRecipient?: { email?: string };
  disputeData?: { disputeStatus?: DisputeStatus };
  transaction?: { status?: TransactionStatus };
};

type InvoicesTableProps = {
  filterCriteria: InvoiceTableQueryProps;
  /** Paid Date is meaningless on the working set that excludes paid invoices. */
  showPaidDate: boolean;
};

const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

// Column id -> the field name the invoices query sorts on.
const SORT_COLUMNS: Record<string, string> = {
  invoiceDate: "invoiceDate",
  invoice: "invoiceNumber",
  dueDate: "dueDate",
};

const InvoicesTable: React.FC<InvoicesTableProps> = (props) => {
  // An invoice email asks the customer to pay, so sending one waits on the company being able to take payments.
  const canTakePayments = useCanTakePayments();
  const { filterCriteria, showPaidDate } = props;

  const { t } = useTranslation();

  const invoiceDrawer = useDrawerRecord<string>();
  const [
    openSendInvoiceConfirmationModal,
    setOpenSendInvoiceConfirmationModal,
  ] = useState(false);

  const [sortBy, setSortBy] = useState("invoiceDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    DEFAULT_SORT_DIRECTION,
  );
  const [selectedInvoice, setSelectedInvoice] = useState<Data>();
  const [openEditSentInvoiceModal, setOpenEditSentInvoiceModal] =
    useState(false);
  const [openRefundInvoiceModal, setOpenRefundInvoiceModal] = useState(false);

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );

  const [openSendInvoiceAsEstimateModal, setOpenSendInvoiceAsEstimateModal] =
    useState(false);
  const [openResendFailedNoticeModal, setOpenResendFailedNoticeModal] =
    useState(false);

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        status: filterCriteria.status,
        searchTerm: filterCriteria.searchTerm,
        sortColumn: SORT_COLUMNS[sortBy],
        sortDirection: sortDirection.toUpperCase(),
      });
    }
  }, [filterCriteria, sortBy, sortDirection]);

  const handleSort = (id: string) => {
    if (!SORT_COLUMNS[id]) return;
    setSortDirection(
      id === sortBy
        ? sortDirection === "asc"
          ? "desc"
          : "asc"
        : DEFAULT_SORT_DIRECTION,
    );
    setSortBy(id);
  };

  const extractInvoiceData = (data: any) => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map(mapInvoiceNodeToTableRow);
  };

  const filterInvoiceData = (item: Data) => {
    return filterCriteria.status?.includes(item.status) ?? true;
  };

  const {
    dataArray,
    displayLoading,
    handleScroll,
    refetch: invoiceRefetch,
    isInfiniteScrollFetching,
  } = useInfiniteScroll({
    query: GET_INVOICES,
    initialVariables: defaultPaginationVariables,
    extractData: extractInvoiceData,
    filterFunction: filterInvoiceData,
  });

  const [openInvoicePreview, setOpenInvoicePreviewContainer] =
    React.useState(false);
  const [invoiceIdForSendInvoiceForm, setInvoiceIdForSendInvoiceForm] =
    React.useState<string>();

  const handleOpenInvoicePreview = (id: string) => {
    setInvoiceIdForSendInvoiceForm(id);
    setOpenInvoicePreviewContainer(true);
  };

  const handleOpenInvoiceDetails = (id: string) => invoiceDrawer.show(id);

  const handleExitInvoicePreview = () => {
    setOpenInvoicePreviewContainer(false);
    !invoiceDrawer.open && resetStates();
  };

  const handleExitInvoiceDetails = () => {
    invoiceDrawer.close();
    !openInvoicePreview && resetStates();
    resetInvoiceIdParam();
  };

  const resetStates = () => {
    setInvoiceIdForSendInvoiceForm(undefined);
    setSelectedInvoice(undefined);
  };

  const resetInvoiceIdParam = useGetParamFromUrl(
    "id",
    handleOpenInvoiceDetails,
  );

  const {
    specificLoadingStates,
    handleCancelInvoice,
    handleUncancelInvoice,
    handleOpenEditInvoicePage,
  } = useInvoiceData({
    shouldFetchAttachments: false,
    shouldNotQueryInvoice: true,
  });

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "invoiceDate",
        header: t("Invoice Date"),
        sortId: "invoiceDate",
        width: 160,
        cell: (invoice) => invoice.invoiceDate,
      },
      {
        id: "recipient",
        header: t("Recipient"),
        maxWidth: 160,
        cell: (invoice) => invoice.recipient,
      },
      {
        id: "amount",
        header: t("Amount"),
        align: "right",
        cell: (invoice) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: "var(--type-weight-semibold)" }}
          >
            {invoice.amount}
          </Typography>
        ),
      },
      {
        id: "invoice",
        header: t("Invoice"),
        sortId: "invoice",
        maxWidth: 320,
        cell: (invoice) => invoice.invoice,
      },
      {
        id: "sentDate",
        header: t("Sent Date"),
        maxWidth: 160,
        cell: (invoice) => ({
          content: invoice.sentDate,
          caption: invoice.sentBy || undefined,
        }),
      },
      {
        id: "dueDate",
        header: t("Due Date"),
        sortId: "dueDate",
        cell: (invoice) =>
          invoice.dueDate ? (
            <RenderDateWithPopup
              inputDate={invoice.dueDate}
              paidDate={invoice.paidDate}
            />
          ) : (
            t(invoice.paymentTerms ?? "")
          ),
      },
      ...(showPaidDate
        ? [
            {
              id: "paidDate",
              header: t("Paid Date"),
              cell: (invoice: Data) => invoice.paidDate,
            },
          ]
        : []),
      {
        id: "status",
        header: t("Status"),
        align: "center",
        cell: (invoice) => (
          <RenderInvoiceStatus
            invoiceStatus={invoice.status}
            disputeStatus={invoice?.disputeData?.disputeStatus}
            dueDate={invoice.dueDate}
            microdepositPending={invoice.microdepositPending}
          />
        ),
      },
    ],
    [t, showPaidDate],
  );

  const rowActions = (invoice: Data): RowAction[] => {
    const {
      isInvoiceEditable,
      isInvoiceSendable,
      shouldNotShowUncancel,
      isInvoiceSent,
      isEstimate,
    } = getInvoiceStatusDetails(invoice.status);

    const isAllowRefundInvoice =
      invoice.status === InvoiceStatus.COMPLETE &&
      invoice.transaction?.status === TransactionStatus.COMPLETE;

    return [
      {
        label: t("View Invoice Details"),
        icon: <HugeiconsIcon icon={Invoice03Icon} size={16} />,
        onClick: () => handleOpenInvoiceDetails(invoice.id),
      },
      {
        label: t("View Invoice Preview"),
        icon: <HugeiconsIcon icon={ViewIcon} size={16} />,
        onClick: () => handleOpenInvoicePreview(invoice.id),
      },
      ...(isInvoiceEditable && !isAuditor
        ? [
            {
              label: t("Edit Invoice"),
              icon: <HugeiconsIcon icon={PencilEdit02Icon} size={16} />,
              onClick: () => {
                setSelectedInvoice(invoice);
                if (isInvoiceSent) {
                  setOpenEditSentInvoiceModal(true);
                } else {
                  handleOpenEditInvoicePage(invoice.id);
                }
              },
            },
          ]
        : []),
      ...(isInvoiceSendable && canTakePayments
        ? [
            {
              label: isInvoiceSent ? t("Resend Invoice") : t("Send Invoice"),
              icon: (
                <HugeiconsIcon
                  icon={isInvoiceSent ? RecoveryMailIcon : Mail01Icon}
                  size={16}
                />
              ),
              onClick: () => {
                setSelectedInvoice(invoice);
                setOpenSendInvoiceConfirmationModal(true);
              },
            },
          ]
        : []),
      ...(isInvoiceSendable && !isAuditor
        ? [
            {
              label: isEstimate ? t("Resend Estimate") : t("Send as Estimate"),
              icon: <HugeiconsIcon icon={Estimate01Icon} size={16} />,
              onClick: () => {
                setSelectedInvoice(invoice);
                setOpenSendInvoiceAsEstimateModal(true);
              },
            },
          ]
        : []),
      ...(invoice.status === InvoiceStatus.PAYMENT_FAILED &&
      !isAuditor &&
      canTakePayments
        ? [
            {
              label: t("Re-message Customer"),
              icon: <HugeiconsIcon icon={RecoveryMailIcon} size={16} />,
              onClick: () => {
                setSelectedInvoice(invoice);
                setOpenResendFailedNoticeModal(true);
              },
            },
          ]
        : []),
      ...(isInvoiceEditable && !isAuditor
        ? [
            {
              label: t("Cancel Invoice"),
              icon: <HugeiconsIcon icon={CancelCircleIcon} size={16} />,
              onClick: async () => {
                await handleCancelInvoice(invoice.id);
                invoiceRefetch();
              },
            },
          ]
        : []),
      ...(!shouldNotShowUncancel && !isAuditor
        ? [
            {
              label: t("Uncancel Invoice"),
              icon: <HugeiconsIcon icon={DeletePutBackIcon} size={16} />,
              onClick: async () => {
                await handleUncancelInvoice(invoice.id);
                invoiceRefetch();
              },
            },
          ]
        : []),
      ...(isAllowRefundInvoice && !isAuditor
        ? [
            {
              label: t("refundFeature.refundInvoice"),
              icon: <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={16} />,
              onClick: () => {
                setSelectedInvoice(invoice);
                setOpenRefundInvoiceModal(true);
              },
            },
          ]
        : []),
    ];
  };

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(invoice) => invoice.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(invoice) => handleOpenInvoiceDetails(invoice.id)}
        onScroll={handleScroll}
        emptyState={t("No invoices found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more invoices")}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        sortAlwaysVisible
        stickyHeader
      />

      <LoadingWithoutModal
        open={
          specificLoadingStates.cancelingInvoice ||
          specificLoadingStates.uncancelingInvoice ||
          (specificLoadingStates.updatingInvoice &&
            !openEditSentInvoiceModal) ||
          specificLoadingStates.sendingInvoice
        }
      />

      {invoiceDrawer.record && (
        <InvoiceDetailsDrawer
          open={invoiceDrawer.open}
          invoiceId={invoiceDrawer.record}
          handleCloseInvoiceDetailsDrawer={handleExitInvoiceDetails}
          onAfterClose={invoiceDrawer.afterClose}
        />
      )}

      {openInvoicePreview && invoiceIdForSendInvoiceForm && (
        <InvoicePreviewContainer
          open={openInvoicePreview}
          invoiceId={invoiceIdForSendInvoiceForm}
          onExit={handleExitInvoicePreview}
        />
      )}

      <EditInvoiceConfirmationModal
        openEditSentInvoiceModal={openEditSentInvoiceModal}
        selectedInvoiceId={selectedInvoice?.id}
        onCloseModal={() => setOpenEditSentInvoiceModal(false)}
      />

      <SendInvoiceConfirmationModal
        openSendInvoiceConfirmationModal={openSendInvoiceConfirmationModal}
        invoice={selectedInvoice}
        onCloseModal={() => setOpenSendInvoiceConfirmationModal(false)}
      />

      <SendInvoiceAsEstimateConfirmationModal
        openSendInvoiceAsEstimateModal={openSendInvoiceAsEstimateModal}
        invoice={selectedInvoice}
        onCloseModal={() => setOpenSendInvoiceAsEstimateModal(false)}
      />
      <RefundInvoiceConfirmationModal
        openRefundInvoiceModal={openRefundInvoiceModal}
        invoice={selectedInvoice}
        onCloseModal={(isRefresh) => {
          setOpenRefundInvoiceModal(false);
          // The row carries the transaction status the refund just moved,
          // and the kebab gates on it, so without this the action stays
          // offered against a state that no longer exists.
          if (isRefresh) {
            invoiceRefetch();
          }
        }}
      />
      <ResendFailedPaymentNoticeModal
        open={openResendFailedNoticeModal}
        invoice={selectedInvoice ?? {}}
        onCloseModal={() => setOpenResendFailedNoticeModal(false)}
      />
    </Box>
  );
};

export default InvoicesTable;
